/**
 * WAYFINDING.
 *
 * The named anti-bar on this run is a shipped game whose loudest criticism was that
 * players could not find anything. Both verified depth bars carry it, verbatim, in
 * gauntlet/refs/bar-depth.json: Melvor Idle - "constantly requires either the wiki or
 * discord to progress"; Bloobs Adventure Idle - "You're encouraged to go onto a Wiki
 * hosted elsewhere to learn where to use items. That info is not there" and "No tutorial.
 * Got lost in a cake and gave up."
 *
 * THE PROPERTY THIS LANE HOLDS, in five clauses, each with a test that fails when it
 * stops being true:
 *
 *   W1 REACHABILITY. Every charted place is discovered by drift alone. No place needs an
 *      action, an item, a wiki or a guess to find. A player who does nothing at all still
 *      sees the entire chart fill in. (chart.test.ts: no place has an infinite sighting
 *      distance.)
 *
 *   W2 ONE SELECTION. Any discovered place is reached by exactly one selection from the
 *      chart, from any state the boat can be in - drifting, moored, or mid-journey. There
 *      is no route to plan, no intermediate hop, and no steering. (wayfinding.test.ts.)
 *
 *   W3 BOUNDED JOURNEY, AND AN ETA THAT MEANS SOMETHING. The longest passage between any
 *      two charted places comes in under PASSAGE_CEILING_MINUTES, and every pair on the
 *      chart gets its own ETA. Round 3 held the first half with a clamp, which made the
 *      test the clamp's own postcondition and made 97.37% of the chart's ETAs read exactly
 *      3:00 - the promise was kept and the readout was a constant. Both halves are now
 *      MEASURED over the shipped chart, with a negative control. (journey.test.ts.)
 *
 *   W4 THE READOUT IS NEVER SILENT, AND IT NEVER CHANGES THE KIND OF PROMISE IT MAKES.
 *      `ChartView.wayfinding` is a value, not a nullable one. Inside the charted sea it
 *      names the next landfall with a bearing and a countdown, withholding only the name.
 *      Once the sea is known it makes the SAME promise with the name filled in: the next
 *      place the boat's round will raise, which way it lies, and when.
 *
 *      Round 3 answered the second case with a LAP COUNT of the sea's empty rim - 1,128 of
 *      them by wall-clock hour 960, for 96.34% of the game. That is the state CONTRACT.md
 *      section 1.5d rules against by name, and the counter is deleted rather than renamed.
 *
 *      Round 1 wrote this clause as "BELOW THE CHARTED END there is always a next
 *      landfall", and its tests sampled only below that end. Past it, `next` was null at
 *      hour 52 and still null at hour 1000, while the boat receded from the whole world
 *      on an unbounded spiral. The clause was true and the player was abandoned. The
 *      scope qualifier is gone, the type has no null to return, and wayfinding.test.ts
 *      now samples the far side of the boundary explicitly.
 *
 *   W5 THE EXIT IS ON THE ENTRY SURFACE. The chart is where a player parks the boat, so
 *      the chart is where the control that un-parks it lives. `ChartView.helm.exit` is
 *      present in every state, and `exit.needed` is false in exactly one of them - the
 *      boat already drifting on its natural track. (exits.test.ts, over the reachable
 *      state space.)
 *
 * Together: a new player can find any unlocked place in one selection and at most three
 * minutes, always knows something is coming and roughly when, and can always get back to
 * the sea from the same panel that took them off it. Nobody has to leave the game to play
 * it, and nobody can get stuck inside it.
 */

import type { ChartedPlace } from './chart';
import { CADENCE_MAX_GAP_FRACTION } from './constants';
import { journeyDurationMs } from './journey';
import { modelledHoursAtProgress, progressAtTrackDistance, trackDistanceAtProgress } from './pacing';
import type { WaterRegionId } from './regions';
import type { Degrees, Leagues, Progress, Vec2 } from './units';
import { bearingDeg, distance } from './units';
import type { Standing } from './settled';
import { hoursUntilOverTrack } from './settled';
import type { ExitAvailability, VoyageFix, VoyagePhase } from './voyage';
import { ON_TRACK_EPSILON_LEAGUES, exitAvailability } from './voyage';
import type { VoyageWorld } from './world';
import type { Destination } from './destinations';

export interface ChartRow {
  readonly id: string;
  readonly label: string;
  readonly position: Vec2;
  readonly region: WaterRegionId;
  /** Straight-line distance from the boat, in leagues. */
  readonly distanceLeagues: Leagues;
  /** Compass bearing from the boat, 0 = north, clockwise. */
  readonly bearingDeg: Degrees;
  /** Exactly how long sailing there takes, in seconds. Not an estimate. */
  readonly etaSeconds: number;
  /** The row the boat is already at. A position, not a destination. */
  readonly isHere: boolean;
}

/** Inside the charted sea: what is coming, which way, and how far off. */
export interface NextLandfallReadout {
  readonly kind: 'next-landfall';
  /** Progression still to earn before it comes into sight. */
  readonly progressRemaining: Progress;
  /** The same distance in hours, under the pacing's own progression curve. */
  readonly modelledHoursRemaining: number;
  /** Leagues of track still to drift. */
  readonly trackLeaguesRemaining: Leagues;
  /** Which way it lies. The name is withheld; the heading is not. */
  readonly bearingDeg: Degrees;
}

/**
 * The sea is known, and the boat keeps its round of it.
 *
 * Deliberately the same shape as `NextLandfallReadout`: a place, a bearing, a time. The
 * only difference is that the place has a NAME now, because the player has been there.
 */
export interface SettledReadout {
  readonly kind: 'settled';
  /** What the boat is doing: lying to at one end of the round, or standing along it. */
  readonly standing: Standing;
  /** The charted place the boat is lying at or passing, when one is within sight. */
  readonly atPlaceId: string | null;
  /** The next charted place the round will raise. Never null: the round always raises one. */
  readonly nextPlaceId: string;
  /** Hours of play until it is raised, under this world's own curve. */
  readonly modelledHoursToNext: number;
  /** Which way it lies from here. */
  readonly bearingDeg: Degrees;
  /** Hours in one full round. The boat's rhythm - a period, never a count of laps run. */
  readonly roundHours: number;
}

export type WayfindingReadout = NextLandfallReadout | SettledReadout;

/** The state of the boat, and the way out of it. Rendered beside the chart, always. */
export interface HelmView {
  readonly phase: VoyagePhase;
  readonly mooredAt: string | null;
  readonly offTrackLeagues: Leagues;
  readonly beyondCharted: boolean;
  /** What the boat's own progression is doing: charted, lying, homeward or seaward. */
  readonly standing: Standing;
  /** Never null. `needed: false` means the boat is already living its progression. */
  readonly exit: ExitAvailability;
}

export interface ChartView {
  readonly discovered: readonly ChartRow[];
  /** Murrin-local choices, excluded from all global chart counts and cadence. */
  readonly destinations: readonly Destination[];
  /** Never null. See W4. */
  readonly wayfinding: WayfindingReadout;
  readonly helm: HelmView;
  readonly discoveredCount: number;
  readonly chartedCount: number;
  /** Fraction of the charted sea already found, in [0, 1]. */
  readonly completion: number;
}

function toRow(world: VoyageWorld, place: ChartedPlace, fix: VoyageFix): ChartRow {
  const d = distance(fix.position, place.position);
  return {
    id: place.id,
    label: place.label,
    position: place.position,
    region: place.region,
    distanceLeagues: d,
    bearingDeg: bearingDeg(fix.position, place.position),
    etaSeconds: journeyDurationMs(d, world.journeyLaw) / 1000,
    isHere: fix.mooredAt === place.id || d <= ON_TRACK_EPSILON_LEAGUES,
  };
}

/**
 * What the player sees when the chart opens: everything already found, nearest first;
 * one line about what is coming next, or about the sea being complete; and the state of
 * the boat together with the control that returns it to the sea.
 *
 * IT TAKES THE WHOLE FIX. Round 1's signature was `(world, progress, boat)` - a
 * progression number and a coordinate - which is exactly why the panel could not say
 * that the boat was moored or offer to un-moor it: the two facts were not in the room.
 * Passing the fix is what makes W5 structural rather than remembered.
 */
export function chartView(world: VoyageWorld, fix: VoyageFix): ChartView {
  const progress = Math.max(0, fix.progress);
  const reached = trackDistanceAtProgress(progress, world.pacing);

  const discovered: ChartRow[] = [];
  let next: ChartedPlace | null = null;
  for (const place of world.chart.places) {
    if (!Number.isFinite(place.sightTrackDistance)) continue;
    if (place.sightTrackDistance <= reached) discovered.push(toRow(world, place, fix));
    else if (next === null) next = place;
  }

  discovered.sort((a, b) => {
    if (a.distanceLeagues !== b.distanceLeagues) return a.distanceLeagues - b.distanceLeagues;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  const charted = world.chart.places.filter((p) => Number.isFinite(p.sightTrackDistance)).length;

  return {
    discovered,
    destinations: world.destinations,
    wayfinding: next === null ? settled(world, fix) : nextLandfall(world, next, progress, reached, fix),
    helm: {
      phase: fix.phase,
      mooredAt: fix.mooredAt,
      offTrackLeagues: fix.offTrackLeagues,
      beyondCharted: fix.beyondCharted,
      standing: fix.standing,
      exit: exitAvailability(world, fix),
    },
    discoveredCount: discovered.length,
    chartedCount: charted,
    completion: charted === 0 ? 1 : discovered.length / charted,
  };
}

function nextLandfall(
  world: VoyageWorld,
  next: ChartedPlace,
  progress: Progress,
  reached: Leagues,
  fix: VoyageFix,
): NextLandfallReadout {
  const progressAtNext = progressAtTrackDistance(next.sightTrackDistance, world.pacing);
  return {
    kind: 'next-landfall',
    progressRemaining: progressAtNext - progress,
    modelledHoursRemaining:
      modelledHoursAtProgress(progressAtNext, world.pacing) - modelledHoursAtProgress(progress, world.pacing),
    trackLeaguesRemaining: next.sightTrackDistance - reached,
    bearingDeg: bearingDeg(fix.position, next.position),
  };
}

/**
 * THE SETTLED READOUT, and the one thing it must never do is count.
 *
 * `atPlaceId` is the charted place within the sighting radius of the boat, which is the
 * same relation the chart uses for discovery - so "lying at" means the same thing here as
 * "in sight" does everywhere else, rather than being a second, looser idea of nearness.
 *
 * `nextPlaceId` is found by asking the round when it will next be over each charted
 * place's own sighting distance and taking the soonest. That is a question about the
 * ROUND, answered with the CHART, so neither module can quietly agree with itself.
 */
function settled(world: VoyageWorld, fix: VoyageFix): SettledReadout {
  const sea = world.sea;
  // THE NATURAL STATE COMES OFF THE FIX. An earlier draft re-solved it here, which meant a
  // second call into the curve's inverse on every frame AND a second answer that could
  // disagree with the boat's own. The fix already carries both quantities this needs.
  const naturalPosition = fix.trackPosition;

  let atPlaceId: string | null = null;
  let nearest = world.chart.sightingRadiusLeagues;
  let next: ChartedPlace | null = null;
  let soonest = Infinity;

  for (const place of world.chart.places) {
    if (!Number.isFinite(place.sightTrackDistance)) continue;
    const d = distance(naturalPosition, place.position);
    if (d <= nearest) {
      nearest = d;
      atPlaceId = place.id;
    }
    const hours = hoursUntilOverTrack(sea, fix.roundPhaseHours, place.sightTrackDistance);
    // Strictly sooner, so a tie between two places is broken by chart order rather than
    // by float noise - the boat is over the home place at the exact instant it is over
    // anything else that shares a sighting distance.
    if (hours > 0 && hours < soonest) {
      soonest = hours;
      next = place;
    }
  }

  const fallback = world.chart.places[0];
  const target = next ?? fallback;
  return {
    kind: 'settled',
    standing: fix.standing,
    atPlaceId,
    nextPlaceId: target?.id ?? '',
    modelledHoursToNext: Number.isFinite(soonest) ? soonest : 0,
    bearingDeg: target === undefined ? 0 : bearingDeg(fix.position, target.position),
    roundHours: sea.roundHours,
  };
}

/**
 * The largest stretch of progression, as a fraction of the charted span, in which nothing
 * new comes into sight.
 *
 * IT IS COMPARED AGAINST CADENCE_MAX_GAP_FRACTION AND NOTHING ELSE. Round 1 compared it
 * against a bound this module computed for itself, widened by two sighting radii, and the
 * layout exceeded the measured Melvor bar while the test passed. `cadenceBoundFraction`
 * is gone: a bound that moves with the thing it constrains is not a bound. What replaced
 * it is on the other side - places.ts now SOLVES the layout so that sighting distances
 * land on the cadence fractions, which is where the two-radius blur actually belonged.
 */
export function largestDiscoveryGapFraction(world: VoyageWorld): number {
  const sights = world.chart.places
    .map((p) => p.sightTrackDistance)
    .filter((s) => Number.isFinite(s))
    .sort((a, b) => a - b);

  const span = world.chart.lastSightTrackDistance;
  if (!(span > 0) || sights.length < 2) return 0;

  let largest = 0;
  for (let i = 1; i < sights.length; i += 1) {
    const a = sights[i - 1];
    const b = sights[i];
    if (a === undefined || b === undefined) continue;
    largest = Math.max(largest, b - a);
  }
  return largest / span;
}

/**
 * Wall-clock hours at which each charted place comes into sight, in chart order.
 *
 * MODELLED, because it reads the pacing's own curve; the hours are what a continuously
 * present player experiences, which is also what an absent one does - see the offline
 * invariant.
 */
export function landfallHours(world: VoyageWorld): readonly number[] {
  return world.chart.places.map((p) =>
    modelledHoursAtProgress(progressAtTrackDistance(p.sightTrackDistance, world.pacing), world.pacing),
  );
}

/**
 * The longest a player ever goes with nothing new coming into sight, IN HOURS.
 *
 * The sibling of `largestDiscoveryGapFraction`, and not a substitute for it: the fraction
 * is the measured Melvor cadence bar, and this is the named anti-bar - a waiting-
 * simulator endgame - in the unit that anti-bar is written in. A fraction is blind to it,
 * because every span has the same fractions however long the span is.
 *
 * Compared against LANDFALL_GAP_HOLDS_MAX offline holds and against nothing else; see
 * span.test.ts.
 */
export function largestLandfallGapHours(world: VoyageWorld): number {
  const hours = landfallHours(world).filter((h) => Number.isFinite(h)).sort((a, b) => a - b);
  let largest = 0;
  for (let i = 1; i < hours.length; i += 1) {
    const a = hours[i - 1];
    const b = hours[i];
    if (a === undefined || b === undefined) continue;
    largest = Math.max(largest, b - a);
  }
  return largest;
}

/** The measured Melvor cadence, unmodified. Exported so the bar is quotable as one value. */
export const CADENCE_BAR_FRACTION = CADENCE_MAX_GAP_FRACTION;
